import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Textarea } from "@/components/base/FormControls";
import { useFormules } from "@/store/formules/FormulesProvider";
import { formatFcfa } from "@/utils/format";
import {
  LIMITE_KEYS,
  LIMITE_LABELS,
  type FormuleCatalogue,
  type FormuleId,
  type FormuleLimites,
} from "@/config/formules";

interface FormuleFormProps {
  open: boolean;
  onClose: () => void;
  formule: FormuleCatalogue | null;
}

interface FormState {
  nom: string;
  tagline: string;
  cta: string;
  prixMensuel: string;
  prixAnnuel: string;
  /** Valeur saisie par limite : chaîne vide = illimité. */
  limites: Record<keyof FormuleLimites, string>;
}

const EMPTY_LIMITES: Record<keyof FormuleLimites, string> = {
  proprietaires: "",
  immeubles: "",
  biens: "",
  locataires: "",
  contrats: "",
  employes: "",
};

const EMPTY_STATE: FormState = {
  nom: "",
  tagline: "",
  cta: "",
  prixMensuel: "0",
  prixAnnuel: "0",
  limites: EMPTY_LIMITES,
};

/**
 * Édition d'une formule côté Super Admin.
 *
 * Les tarifs (mensuel / annuel) sont modifiables et ENREGISTRÉS DANS SUPABASE,
 * qui reste la source de vérité unique. L'écriture est protégée côté serveur par
 * la policy RLS `formules_update_admin` (`is_super_admin()`), et toute
 * modification de tarif ou d'activation est journalisée dans `journal_audit`.
 *
 * Une confirmation explicite est requise avant l'enregistrement (règle #5).
 */
export default function FormuleForm({ open, onClose, formule }: FormuleFormProps) {
  const { updateFormule } = useFormules();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !formule) return;
    const limites = { ...EMPTY_LIMITES };
    LIMITE_KEYS.forEach((key) => {
      const value = formule.limites[key];
      limites[key] = value === null ? "" : String(value);
    });
    setForm({
      nom: formule.nom,
      tagline: formule.tagline,
      cta: formule.cta,
      prixMensuel: String(formule.prixMensuel),
      prixAnnuel: String(formule.prixAnnuel),
      limites,
    });
    setErrors({});
    setConfirming(false);
  }, [open, formule]);

  const prixChange = useMemo(() => {
    if (!formule) return false;
    return (
      Math.max(0, Number(form.prixMensuel) || 0) !== formule.prixMensuel ||
      Math.max(0, Number(form.prixAnnuel) || 0) !== formule.prixAnnuel
    );
  }, [formule, form.prixMensuel, form.prixAnnuel]);

  if (!formule) return null;

  const update = (key: keyof Omit<FormState, "limites">, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateLimite = (key: keyof FormuleLimites, value: string) => {
    setForm((prev) => ({ ...prev, limites: { ...prev.limites, [key]: value } }));
  };

  const handleRequestSave = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.nom.trim()) nextErrors.nom = "Le nom de la formule est obligatoire.";
    if (Number(form.prixMensuel) < 0) nextErrors.prixMensuel = "Le prix mensuel est invalide.";
    if (Number(form.prixAnnuel) < 0) nextErrors.prixAnnuel = "Le prix annuel est invalide.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setConfirming(true);
  };

  const handleConfirm = () => {
    const limites: Partial<FormuleLimites> = {};
    LIMITE_KEYS.forEach((key) => {
      const raw = form.limites[key].trim();
      limites[key] = raw === "" ? null : Math.max(0, Number(raw) || 0);
    });

    updateFormule(formule.id as FormuleId, {
      nom: form.nom.trim(),
      tagline: form.tagline.trim(),
      cta: form.cta.trim() || "Choisir cette formule",
      prixMensuel: Math.max(0, Number(form.prixMensuel) || 0),
      prixAnnuel: Math.max(0, Number(form.prixAnnuel) || 0),
      limites,
    });
    setConfirming(false);
    onClose();
  };

  const nouveauMensuel = Math.max(0, Number(form.prixMensuel) || 0);
  const nouveauAnnuel = Math.max(0, Number(form.prixAnnuel) || 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={confirming ? "Confirmer la modification" : `Modifier — ${formule.nom}`}
      description={
        confirming
          ? undefined
          : "Ces valeurs alimentent la page publique Tarifs, l'inscription et les espaces Propriétaire et Agence."
      }
      footer={
        confirming ? (
          <>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
              Confirmer
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleRequestSave}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-save-line text-base" aria-hidden="true" />
              Enregistrer
            </button>
          </>
        )
      }
    >
      {confirming ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-price-tag-3-line text-xl" aria-hidden="true" />
            </span>
            <div className="flex flex-col gap-1.5 text-sm leading-relaxed text-foreground-700">
              <p>Vous êtes sur le point de modifier les tarifs de la formule « {formule.nom} ».</p>
              <p>
                Les nouveaux tarifs pourront être utilisés pour les prochaines souscriptions, renouvellements ou
                changements de formule.
              </p>
              <p className="font-label font-medium text-foreground-900">Confirmer ?</p>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-left">
                  <th scope="col" className="px-4 py-2.5 font-label font-semibold text-foreground-500">
                    Tarif
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right font-label font-semibold text-foreground-500">
                    Actuel
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right font-label font-semibold text-foreground-500">
                    Nouveau
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200">
                  <td className="px-4 py-2.5 text-foreground-700">Prix mensuel</td>
                  <td className="px-4 py-2.5 text-right text-foreground-500">{formatFcfa(formule.prixMensuel)}</td>
                  <td className="px-4 py-2.5 text-right font-label font-medium text-foreground-950">
                    {formatFcfa(nouveauMensuel)}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5 text-foreground-700">Prix annuel</td>
                  <td className="px-4 py-2.5 text-right text-foreground-500">{formatFcfa(formule.prixAnnuel)}</td>
                  <td className="px-4 py-2.5 text-right font-label font-medium text-foreground-950">
                    {formatFcfa(nouveauAnnuel)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs leading-relaxed text-foreground-600">
            <i className="ri-information-line mt-0.5 text-sm text-foreground-400" aria-hidden="true" />
            {prixChange
              ? "Les abonnements et paiements déjà enregistrés conservent leurs montants historiques. Seules les prochaines opérations utiliseront les nouveaux tarifs."
              : "Aucune modification de tarif : seuls les autres champs seront mis à jour."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nom affiché de la formule" htmlFor="formule-nom" required error={errors.nom}>
              <Input
                id="formule-nom"
                value={form.nom}
                onChange={(event) => update("nom", event.target.value)}
                invalid={Boolean(errors.nom)}
                maxLength={60}
              />
            </Field>
            <Field label="Libellé du bouton (CTA)" htmlFor="formule-cta">
              <Input
                id="formule-cta"
                value={form.cta}
                onChange={(event) => update("cta", event.target.value)}
                placeholder="Choisir cette formule"
                maxLength={60}
              />
            </Field>
            <Field label="Prix mensuel (FCFA)" htmlFor="formule-mensuel" error={errors.prixMensuel}>
              <Input
                id="formule-mensuel"
                type="number"
                min={0}
                value={form.prixMensuel}
                onChange={(event) => update("prixMensuel", event.target.value)}
                invalid={Boolean(errors.prixMensuel)}
              />
            </Field>
            <Field
              label="Prix annuel (FCFA)"
              htmlFor="formule-annuel"
              error={errors.prixAnnuel}
              hint="Valeur indépendante enregistrée telle quelle. La remise annuelle affichée en découle."
            >
              <Input
                id="formule-annuel"
                type="number"
                min={0}
                value={form.prixAnnuel}
                onChange={(event) => update("prixAnnuel", event.target.value)}
                invalid={Boolean(errors.prixAnnuel)}
              />
            </Field>
            <Field label="Tagline" htmlFor="formule-tagline" className="sm:col-span-2">
              <Textarea
                id="formule-tagline"
                value={form.tagline}
                onChange={(event) => update("tagline", event.target.value)}
                className="min-h-[72px]"
                maxLength={160}
              />
            </Field>
          </div>

          <div>
            <p className="text-sm font-label font-medium text-foreground-800">Limites</p>
            <p className="mt-0.5 text-xs text-foreground-500">Laissez un champ vide pour marquer « Illimité ».</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {LIMITE_KEYS.map((key) => (
                <Field key={key} label={LIMITE_LABELS[key]} htmlFor={`formule-limite-${key}`}>
                  <Input
                    id={`formule-limite-${key}`}
                    type="number"
                    min={0}
                    value={form.limites[key]}
                    onChange={(event) => updateLimite(key, event.target.value)}
                    placeholder="Illimité"
                  />
                </Field>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}